import React, { useRef, useMemo, useState, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './StepProcess.css';

// Move static data outside component to prevent recreation on each render
const STEPS_DATA = [
  {
    title: "15 Minute Strategy Call",
    description: "Schedule a personal consultation where we discuss your catalog goals, current challenges, and identify the most promising revenue opportunities for your music.",
    icon: "video.svg"
  },
  {
    title: "Catalog Deep Dive", 
    description: "Our team manually connects to your streaming accounts and thoroughly reviews your catalog to identify revenue gaps, distribution issues, and optimization opportunities across all markets.",
    icon: "flasher.svg"
  },
  {
    title: "Finished PDF Analysis Delivery",
    description: "Receive your comprehensive Song Audit report with actionable insights, revenue recovery roadmap, and step-by-step implementation guide delivered directly to your inbox.",
    icon: "signature.svg"
  },
  {
    title: "Start Earning More Streaming Revenue",
    description: "Implement our recommendations to unlock hidden revenue streams, fix distribution gaps, and maximize your catalog's earning potential across all streaming platforms.",
    icon: "receive-money.svg"
  }
];

// Static path string to avoid recreation
const SVG_PATH = "M 400 50 C 400 50, 600 180, 600 320 C 600 460, 200 520, 200 660 C 200 800, 600 860, 600 1000 C 600 1140, 200 1200, 200 1340 C 200 1480, 400 1550, 400 1550";

// Animation variants to prevent inline object creation
const headerVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0 }
};

const stepVariants = {
  hidden: (isLeftSide) => ({ 
    opacity: 0, 
    x: isLeftSide ? -50 : 50 
  }),
  visible: { 
    opacity: 1, 
    x: 0 
  }
};

const stepNumberVariants = {
  hidden: { scale: 0 },
  visible: { scale: 1 }
};

const stepIconVariants = {
  hidden: { opacity: 0, scale: 0, rotate: -180 },
  visible: { opacity: 1, scale: 1, rotate: 0 }
};

// Debounce hook for performance optimization
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const StepProcess = () => {
  const containerRef = useRef(null);
  const [waveAnimationActive, setWaveAnimationActive] = useState(false);
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start center', 'end center']
  });

  // Debounce scroll progress for better performance
  const debouncedScrollProgress = useDebounce(scrollYProgress.get(), 16); // ~60fps

  // Transform scroll progress to path drawing and end point opacity
  const pathLength = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const endPointOpacity = useTransform(scrollYProgress, [0.8, 1], [0, 1]);

  // Memoize step calculations to prevent recreation on each render
  const stepPositions = useMemo(() => 
    STEPS_DATA.map((_, index) => ({
      yPosition: 300 + (index * 400),
      isLeftSide: index % 2 !== 0
    })),
    []
  );

  // Performance optimization: Update scroll progress with debounce
  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', () => {
      // Debounced scroll handling happens automatically via useDebounce
    });

    return () => unsubscribe();
  }, [scrollYProgress]);

  // Wave animation trigger
  useEffect(() => {
    const unsubscribe = endPointOpacity.on('change', (latest) => {
      if (latest >= 0.8 && !waveAnimationActive) {
        setWaveAnimationActive(true);
      }
    });

    return () => unsubscribe();
  }, [endPointOpacity, waveAnimationActive]);

  return (
    <div className="step-process-container" ref={containerRef}>
      <div className="step-process-content">
        {/* Header */}
        <motion.div 
          className="step-process-header"
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h2 className="step-process-title">Our 4-Step Revenue Process</h2>
          <p className="step-process-subtitle">
            From personal consultation to increased revenue - discover how our expert team manually analyzes your music catalog to unlock and implement hidden revenue opportunities.
          </p>
        </motion.div>

        {/* SVG Path Container */}
        <div className="step-process-path-container">
          <svg
            className="step-process-svg"
            viewBox="0 0 800 1600"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Gradient definition moved to top for better SVG performance */}
            <defs>
              <linearGradient id="stepGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6B5B95" />
                <stop offset="25%" stopColor="#9B7EDE" />
                <stop offset="50%" stopColor="#B8A9FF" />
                <stop offset="75%" stopColor="#7B6BAF" />
                <stop offset="100%" stopColor="#5A4A6C" />
              </linearGradient>
            </defs>

            {/* Background path for visual reference */}
            <path
              d={SVG_PATH}
              stroke="rgba(107, 91, 149, 0.2)"
              strokeWidth="3"
              fill="none"
              strokeDasharray="5,5"
            />
            
            {/* Animated main path */}
            <motion.path
              d={SVG_PATH}
              stroke="url(#stepGradient)"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ 
                pathLength,
                filter: "drop-shadow(0 0 12px rgba(107, 91, 149, 0.8)) drop-shadow(0 0 24px rgba(107, 91, 149, 0.4)) drop-shadow(0 0 36px rgba(155, 126, 222, 0.3))"
              }}
              initial={{ pathLength: 0 }}
            />

            {/* End point */}
            <motion.circle
              cx="400"
              cy="1550"
              r="6"
              fill="#6B5B95"
              style={{ 
                opacity: endPointOpacity,
                filter: "drop-shadow(0 0 8px rgba(107, 91, 149, 0.8)) drop-shadow(0 0 16px rgba(155, 126, 222, 0.5))"
              }}
            />

            {/* Wave animations triggered when end point is reached */}
            {waveAnimationActive && [1, 2, 3].map((waveIndex) => (
              <motion.circle
                key={`wave-${waveIndex}`}
                cx="400"
                cy="1550"
                r="6"
                stroke="#6B5B95"
                strokeWidth="2"
                fill="none"
                initial={{ r: 6, opacity: 0 }}
                animate={{
                  r: [6, 40 + (waveIndex * 20)],
                  opacity: [0.8, 0]
                }}
                transition={{
                  duration: 2,
                  delay: waveIndex * 0.3,
                  repeat: Infinity,
                  repeatDelay: 1,
                  ease: "easeOut"
                }}
              />
            ))}
          </svg>

          {/* Step indicators and content */}
          {STEPS_DATA.map((step, index) => {
            const { yPosition, isLeftSide } = stepPositions[index];

            return (
              <div
                key={step.icon} // Use stable key instead of index
                className={`step-indicator ${isLeftSide ? 'step-left' : 'step-right'}`}
                style={{
                  position: 'absolute',
                  top: `${yPosition}px`,
                  left: isLeftSide ? '50px' : 'auto',
                  right: isLeftSide ? 'auto' : '50px',
                  transform: 'translateY(-50%)',
                  zIndex: 20
                }}
              >
                {/* Step number circle */}
                <div 
                  className="step-number"
                >
                  <div className='step-number-text' style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%'}}>
                    <p style={{fontSize: '1.2rem', fontWeight: 'bold'}}>Step {index + 1}</p>
                  </div>
                </div>

                {/* Step content */}
                <div className="step-content">
                  {/* Step Icon */}
                  <div 
                    className={`step-icon ${isLeftSide ? 'icon-left' : 'icon-right'}`}
                  >
                    <img 
                      src={`/img/${step.icon}`} 
                      alt={`Step ${index + 1} Icon`}
                      loading="lazy"
                      decoding="async"
                      width="48"
                      height="48"
                    />
                  </div>

                  <motion.h3 
                    className="step-title"
                    initial={{ opacity: 0, y: 5 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-150px" }}
                    transition={{ 
                      duration: 0.7, 
                      delay: index * 0.15 + 0.2,
                      ease: "easeOut"
                    }}
                  >
                    {step.title}
                  </motion.h3>
                  <motion.p 
                    className="step-description"
                    initial={{ opacity: 0, y: 3 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-150px" }}
                    transition={{ 
                      duration: 0.7, 
                      delay: index * 0.15 + 0.3,
                      ease: "easeOut"
                    }}
                  >
                    {step.description}
                  </motion.p>
                </div>

                {/* Connecting line to path */}
                <div className={`step-connector ${isLeftSide ? 'connector-left' : 'connector-right'}`}></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepProcess; 